import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';

import { LOGIN_MUTATION } from '../services/graphql/mutations.js';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice.js';
import { addAlert } from '../store/slices/uiSlice.js';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { loading } = useSelector((state) => state.auth);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // GraphQL mutation
  const [loginMutation] = useMutation(LOGIN_MUTATION);

  // Validation according to Login.md requirements
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation - must be email format and not null (Login.md requirement)
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de correo electrónico inválido';
    }
    
    // Password validation - at least 6 chars and not null (Login.md requirement)
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    dispatch(loginStart());

    try {
      const { data, errors } = await loginMutation({
        variables: {
          input: {
            email: formData.email.toLowerCase().trim(),
            password: formData.password,
          },
        },
        errorPolicy: 'all',
      });

      // Check if we have GraphQL errors but no data
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      // Check if data is null or doesn't have login
      if (!data || !data.login) {
        throw new Error('Verifique usuario y contraseña');
      }

      const { token, refreshToken, user, message } = data.login;

      // Store refresh token in localStorage
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      dispatch(loginSuccess({ token, user }));
      
      // Show success message as per Login.md: "Bienvenido al sistema"
      dispatch(addAlert({
        type: 'success',
        title: 'Éxito',
        message: message || 'Bienvenido al sistema',
      }));

      // Redirect to intended page or dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });

    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Verifique usuario y contraseña';
      
      // Check if it's a GraphQL error
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = 'Error de conexión. Intente nuevamente.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for specific error types after initial errorMessage assignment
      if (errorMessage.includes('inactivo')) {
        // Inactive user message as per Register.jsx implementation
        errorMessage = 'Su usuario está inactivo, favor contactar al administrador para activación de cuenta';
      }
      
      dispatch(loginFailure(errorMessage));
      
      dispatch(addAlert({
        type: 'error',
        title: 'Error de Autenticación',
        message: errorMessage,
      }));
    }
  };

  return (
<div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex">
      {/* Left Pane: Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-200 to-green-800 text-white items-center justify-center p-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          {/* Subtle background pattern or illustration */}
          <svg className="w-full h-full" fill="none" viewBox="0 0 400 400">
            <defs>
              <pattern id="pattern-circles" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.1)" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)" />
          </svg>
        </div>
        <div className="text-center z-10">
          <div className="mb-16">
            <div className="w-auto h-auto mx-auto max-w-56 max-h-56">
              <img src="/ACTrackLogo.png" alt="Container" className="w-full h-full object-contain" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-12">Bienvenido a <span className="text-cyan-300">ACTrack</span></h1>
          <p className="text-xl font-light">
            Tu plataforma integral para la gestión y seguimiento de envíos.
          </p>
        </div>
        <div className="absolute bottom-0 p-12 text-sm text-cyan-300">
          <p>&copy; {new Date().getFullYear()} ACTrack. Todos los derechos reservados.</p>
        </div>
      </div>
      {/* Right Pane: Login */}

<div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
  <div className="w-full max-w-md lg:max-w-md space-y-8">
        <div className="text-center">
          {/* Title as per Login.md: "Sistema de Status de Cargas" */}
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Sistema de Status de Cargas
          </h1>
          <p className="text-secondary-600">
            Ingrese sus credenciales para acceder al sistema
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field */}
              <div className="form-group">
                {/* Label as per Login.md: "Correo electrónico" */}
                <label htmlFor="email" className="form-label">
                  Correo electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`form-input pl-10 ${errors.email ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su correo electrónico" // Placeholder as per Login.md
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.email}</span>
                  </div>
                )}
              </div>

              {/* Password field */}
              <div className="form-group">
                {/* Label as per Login.md: "Contraseña" */}
                <label htmlFor="password" className="form-label">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className={`form-input pl-10 pr-10 ${errors.password ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su contraseña" // Placeholder as per Login.md
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.password}</span>
                  </div>
                )}
              </div>

              {/* Forgot password link */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200"
                >
                  Olvidó su contraseña? {/* Link text as per Login.md */}
                </Link>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ingresando...
                  </>
                ) : (
                  'Ingresar' // Button text as per Login.md
                )}
              </button>

              {/* Register link */}
              <div className="text-center">
                <p className="text-sm text-secondary-600">
                  {/* Link text as per Login.md: "No se ha registrado? Regístrese acá..." */}
                  No se ha registrado?{' '}
                  <Link
                    to="/register"
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Regístrese acá...
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-secondary-500">
            © 2025 ACTrack. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Login;